import { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Switch,
  ActivityIndicator,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { NotificationPreferences } from "@/types";
import { useNotifications } from "@/lib/notifications/useNotifications";

/**
 * Default notification preferences
 */
const DEFAULT_PREFERENCES: NotificationPreferences = {
  push_enabled: true,
  in_app_enabled: true,
  categories: {
    announcements: true,
    game_updates: true,
    score_updates: true,
    team_updates: true,
    chat_messages: true,
    reminders: true,
    birthdays: true,
  },
};

/**
 * Notification category info
 */
interface CategoryInfo {
  key: keyof NotificationPreferences["categories"];
  title: string;
  description: string;
  icon: keyof typeof Ionicons.glyphMap;
  color: string;
}

const NOTIFICATION_CATEGORIES: CategoryInfo[] = [
  {
    key: "announcements",
    title: "Aankondigingen",
    description: "Belangrijke berichten van de organisatie",
    icon: "megaphone-outline",
    color: "#EF4444",
  },
  {
    key: "game_updates",
    title: "Spel updates",
    description: "Start, pauze en einde van spellen",
    icon: "game-controller-outline",
    color: "#8B5CF6",
  },
  {
    key: "score_updates",
    title: "Score updates",
    description: "Punten updates voor je team",
    icon: "trophy-outline",
    color: "#F59E0B",
  },
  {
    key: "team_updates",
    title: "Team updates",
    description: "Wijzigingen in je team",
    icon: "people-outline",
    color: "#10B981",
  },
  {
    key: "chat_messages",
    title: "Chat berichten",
    description: "Nieuwe berichten in chats",
    icon: "chatbubble-outline",
    color: "#3B82F6",
  },
  {
    key: "reminders",
    title: "Herinneringen",
    description: "Agenda en activiteit herinneringen",
    icon: "alarm-outline",
    color: "#EC4899",
  },
  {
    key: "birthdays",
    title: "Verjaardagen",
    description: "Meldingen over verjaardagen van familieleden",
    icon: "gift-outline",
    color: "#F59E0B",
  },
];

/**
 * Toggle item component for master toggles
 */
interface MasterToggleProps {
  icon: keyof typeof Ionicons.glyphMap;
  iconColor: string;
  title: string;
  description: string;
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
}

const MasterToggle = ({
  icon,
  iconColor,
  title,
  description,
  value,
  onValueChange,
  disabled = false,
}: MasterToggleProps) => {
  const colors = useThemeColors();

  return (
    <View
      className="px-4 py-4 flex-row items-center"
      style={{
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
      }}
    >
      <View
        className="w-10 h-10 rounded-full items-center justify-center mr-3"
        style={{ backgroundColor: `${iconColor}15` }}
      >
        <Ionicons name={icon} size={20} color={iconColor} />
      </View>
      <View className="flex-1 mr-3">
        <Text className="font-medium" style={{ color: colors.text }}>
          {title}
        </Text>
        <Text className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
          {description}
        </Text>
      </View>
      <Switch
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        trackColor={{ false: colors.border, true: colors.primaryLight }}
        thumbColor={value ? colors.primary : colors.textTertiary}
      />
    </View>
  );
};

/**
 * Category toggle component
 */
interface CategoryToggleProps {
  category: CategoryInfo;
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
}

const CategoryToggle = ({
  category,
  value,
  onValueChange,
  disabled = false,
}: CategoryToggleProps) => {
  const colors = useThemeColors();

  return (
    <View
      className="px-4 py-4 flex-row items-center"
      style={{
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <View
        className="w-10 h-10 rounded-full items-center justify-center mr-3"
        style={{ backgroundColor: `${category.color}15` }}
      >
        <Ionicons name={category.icon} size={20} color={category.color} />
      </View>
      <View className="flex-1 mr-3">
        <Text className="font-medium" style={{ color: colors.text }}>
          {category.title}
        </Text>
        <Text className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
          {category.description}
        </Text>
      </View>
      <Switch
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        trackColor={{ false: colors.border, true: `${category.color}40` }}
        thumbColor={value ? category.color : colors.textTertiary}
      />
    </View>
  );
};

/**
 * Notification Settings Screen
 */
export default function NotificationSettingsScreen() {
  const router = useRouter();
  const { user, profile, refreshProfile } = useAuth();
  const colors = useThemeColors();
  const {
    permissionStatus,
    handleEnableNotifications,
    handleOpenSettings,
  } = useNotifications();

  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Load current preferences
   */
  useEffect(() => {
    if (profile?.notification_preferences) {
      // Merge with defaults to ensure all keys exist
      setPreferences({
        ...DEFAULT_PREFERENCES,
        ...profile.notification_preferences,
        categories: {
          ...DEFAULT_PREFERENCES.categories,
          ...profile.notification_preferences.categories,
        },
      });
    }
    setIsLoading(false);
  }, [profile]);

  /**
   * Save preferences to database
   */
  const savePreferences = async (newPreferences: NotificationPreferences) => {
    if (!user) return;

    setIsSaving(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          notification_preferences: newPreferences,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (error) throw error;

      // Refresh profile to get updated data
      await refreshProfile();
    } catch (error) {
      console.error("Error saving preferences:", error);
      Alert.alert("Fout", "Kon instellingen niet opslaan");
      // Revert to previous state
      if (profile?.notification_preferences) {
        setPreferences({
          ...DEFAULT_PREFERENCES,
          ...profile.notification_preferences,
          categories: {
            ...DEFAULT_PREFERENCES.categories,
            ...profile.notification_preferences.categories,
          },
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Toggle push notifications
   */
  const handleTogglePush = (value: boolean) => {
    const newPreferences = { ...preferences, push_enabled: value };
    setPreferences(newPreferences);
    savePreferences(newPreferences);
  };

  /**
   * Toggle in-app notifications
   */
  const handleToggleInApp = (value: boolean) => {
    const newPreferences = { ...preferences, in_app_enabled: value };
    setPreferences(newPreferences);
    savePreferences(newPreferences);
  };

  /**
   * Toggle a category
   */
  const handleToggleCategory = (key: keyof NotificationPreferences["categories"], value: boolean) => {
    const newPreferences = {
      ...preferences,
      categories: {
        ...preferences.categories,
        [key]: value,
      },
    };
    setPreferences(newPreferences);
    savePreferences(newPreferences);
  };

  /**
   * Toggle all categories
   */
  const handleToggleAll = (value: boolean) => {
    const newCategories = Object.keys(preferences.categories).reduce(
      (acc, key) => ({
        ...acc,
        [key]: value,
      }),
      {} as NotificationPreferences["categories"]
    );

    const newPreferences = {
      ...preferences,
      categories: newCategories,
    };
    setPreferences(newPreferences);
    savePreferences(newPreferences);
  };

  // Check if all categories are enabled
  const allCategoriesEnabled = Object.values(preferences.categories).every((v) => v);
  const someCategoriesEnabled = Object.values(preferences.categories).some((v) => v);

  // Categories are only active if either push or in-app is enabled
  const categoriesDisabled = !preferences.push_enabled && !preferences.in_app_enabled;

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["top", "bottom"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-2" style={{ color: colors.textSecondary }}>
          Laden...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top", "bottom"]}
    >
      {/* Header */}
      <View
        className="px-4 py-4 flex-row items-center"
        style={{
          backgroundColor: colors.surface,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
        }}
      >
        <Pressable
          onPress={() => router.back()}
          className="mr-3"
          accessibilityRole="button"
          accessibilityLabel="Terug"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </Pressable>
        <View className="flex-1">
          <Text className="text-xl font-bold" style={{ color: colors.text }}>
            Notificaties
          </Text>
          <Text className="text-sm" style={{ color: colors.textSecondary }}>
            Beheer je meldingen
          </Text>
        </View>
        {isSaving && <ActivityIndicator size="small" color={colors.primary} />}
      </View>

      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        {/* OS Permission Status Banner */}
        {permissionStatus === "denied" && (
          <Pressable
            onPress={handleOpenSettings}
            className="mx-4 mt-4 rounded-xl p-4 flex-row items-center"
            style={{ backgroundColor: "#FEE2E2" }}
            accessibilityRole="button"
            accessibilityLabel="Meldingen staan uit, open instellingen"
          >
            <Ionicons name="notifications-off-outline" size={22} color="#EF4444" />
            <View className="flex-1 mx-3">
              <Text className="font-medium text-sm" style={{ color: "#991B1B" }}>
                Meldingen staan uit op je telefoon
              </Text>
              <Text className="text-xs mt-0.5" style={{ color: "#B91C1C" }}>
                Tik hier om je instellingen te openen
              </Text>
            </View>
            <Ionicons name="open-outline" size={18} color="#EF4444" />
          </Pressable>
        )}

        {permissionStatus === "undetermined" && (
          <Pressable
            onPress={handleEnableNotifications}
            className="mx-4 mt-4 rounded-xl p-4 flex-row items-center"
            style={{ backgroundColor: "#EDE9FE" }}
            accessibilityRole="button"
            accessibilityLabel="Meldingen aanzetten"
          >
            <Ionicons name="notifications-outline" size={22} color="#7C3AED" />
            <View className="flex-1 mx-3">
              <Text className="font-medium text-sm" style={{ color: "#5B21B6" }}>
                Zet meldingen aan
              </Text>
              <Text className="text-xs mt-0.5" style={{ color: "#6D28D9" }}>
                Mis geen berichten van je familie!
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={18} color="#7C3AED" />
          </Pressable>
        )}

        {permissionStatus === "granted" && (
          <View
            className="mx-4 mt-4 rounded-xl p-3 flex-row items-center"
            style={{ backgroundColor: "#D1FAE5" }}
          >
            <Ionicons name="checkmark-circle" size={20} color="#10B981" />
            <Text className="ml-2 text-sm font-medium" style={{ color: "#065F46" }}>
              Push meldingen zijn actief
            </Text>
          </View>
        )}

        {/* Master Controls */}
        <View className="mt-4">
          <Text
            className="px-4 py-2 text-sm font-semibold uppercase tracking-wide"
            style={{ color: colors.textTertiary }}
          >
            Algemeen
          </Text>
          <View
            className="rounded-t-xl overflow-hidden mx-4"
            style={{ borderWidth: 1, borderColor: colors.border }}
          >
            <MasterToggle
              icon="notifications-outline"
              iconColor={colors.primary}
              title="Push notificaties"
              description="Ontvang meldingen wanneer de app gesloten is"
              value={preferences.push_enabled}
              onValueChange={handleTogglePush}
            />
          </View>
          <View
            className="rounded-b-xl overflow-hidden mx-4"
            style={{
              borderWidth: 1,
              borderTopWidth: 0,
              borderColor: colors.border,
            }}
          >
            <MasterToggle
              icon="phone-portrait-outline"
              iconColor="#3B82F6"
              title="In-app notificaties"
              description="Toon meldingen binnen de app"
              value={preferences.in_app_enabled}
              onValueChange={handleToggleInApp}
            />
          </View>
        </View>

        {/* Info message when all notifications are off */}
        {categoriesDisabled && (
          <View
            className="mx-4 mt-4 rounded-xl p-4 flex-row items-start"
            style={{ backgroundColor: "#FEF3C7" }}
          >
            <Ionicons name="warning-outline" size={20} color="#F59E0B" />
            <Text className="flex-1 ml-2 text-sm text-amber-800">
              Je ontvangt momenteel geen notificaties. Zet push of in-app notificaties aan om meldingen te ontvangen.
            </Text>
          </View>
        )}

        {/* Category Controls */}
        <View className="mt-6">
          <View className="flex-row items-center justify-between px-4 py-2">
            <Text
              className="text-sm font-semibold uppercase tracking-wide"
              style={{ color: colors.textTertiary }}
            >
              Categorieën
            </Text>
            <Pressable
              onPress={() => handleToggleAll(!allCategoriesEnabled)}
              disabled={categoriesDisabled}
              accessibilityRole="button"
              accessibilityLabel={allCategoriesEnabled ? "Alles uit" : "Alles aan"}
            >
              <Text
                className="text-sm font-medium"
                style={{ color: categoriesDisabled ? colors.textTertiary : colors.primary }}
              >
                {allCategoriesEnabled ? "Alles uit" : "Alles aan"}
              </Text>
            </Pressable>
          </View>

          <View
            className="rounded-xl overflow-hidden mx-4"
            style={{ borderWidth: 1, borderColor: colors.border }}
          >
            {NOTIFICATION_CATEGORIES.map((category, index) => (
              <CategoryToggle
                key={category.key}
                category={category}
                value={preferences.categories[category.key]}
                onValueChange={(value) => handleToggleCategory(category.key, value)}
                disabled={categoriesDisabled}
              />
            ))}
          </View>
        </View>

        {/* Helper text */}
        <View className="px-4 py-4">
          <Text className="text-sm text-center" style={{ color: colors.textTertiary }}>
            Wijzigingen worden automatisch opgeslagen
          </Text>
        </View>

        <View className="h-8" />
      </ScrollView>
    </SafeAreaView>
  );
}
